import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';
import Modal from '../components/Modal';

const emptyProgram = {
  slug: '',
  title: '',
  description: '',
  show_in_menu: false,
  menu_label: '',
  menu_order: 0,
};

export default function AdminTopicProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [programModal, setProgramModal] = useState(false);
  const [programForm, setProgramForm] = useState(emptyProgram);
  const [savingProgram, setSavingProgram] = useState(false);
  const [itemModal, setItemModal] = useState(false);
  const [itemForm, setItemForm] = useState({
    tag_slug: '',
    title: '',
    description: '',
    exam_number: '',
    study_order: 0,
  });
  const [editingItemId, setEditingItemId] = useState(null);
  const [typeModal, setTypeModal] = useState(false);
  const [typeItemId, setTypeItemId] = useState(null);
  const [typeParentItemId, setTypeParentItemId] = useState(null);
  const [typeForm, setTypeForm] = useState({ tag_slug: '', label: '', sort_order: 0 });
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [tagSearch, setTagSearch] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [formError, setFormError] = useState('');

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tasks/topic-programs/manage/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      setPrograms(data);
    } catch (e) {
      setError('Не удалось загрузить программы.');
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (slug) => {
    if (!slug) return;
    setDetailLoading(true);
    try {
      const res = await api.get(`/tasks/topic-programs/manage/${encodeURIComponent(slug)}/`);
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  useEffect(() => {
    if (selectedSlug) fetchDetail(selectedSlug);
    else setDetail(null);
  }, [selectedSlug, fetchDetail]);

  useEffect(() => {
    let cancelled = false;
    const q = tagSearch.trim();
    if (q.length < 1) {
      setTagSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/tasks/editor/tags/', { params: { search: q } });
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) setTagSuggestions(data.slice(0, 20));
      } catch {
        if (!cancelled) setTagSuggestions([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [tagSearch]);

  async function saveProgramMeta() {
    if (!detail?.slug) return;
    setSavingProgram(true);
    setFormError('');
    try {
      await api.patch(`/tasks/topic-programs/manage/${encodeURIComponent(detail.slug)}/`, {
        title: detail.title,
        description: detail.description,
        show_in_menu: detail.show_in_menu,
        menu_label: detail.menu_label,
        menu_order: detail.menu_order,
      });
      await fetchPrograms();
      await fetchDetail(detail.slug);
    } catch (e) {
      setFormError(e.response?.data ? JSON.stringify(e.response.data) : 'Ошибка сохранения');
    } finally {
      setSavingProgram(false);
    }
  }

  function openCreateProgram() {
    setProgramForm({ ...emptyProgram });
    setFormError('');
    setProgramModal(true);
  }

  async function submitCreateProgram(e) {
    e.preventDefault();
    setSavingProgram(true);
    setFormError('');
    try {
      const res = await api.post('/tasks/topic-programs/manage/', {
        ...programForm,
        menu_order: Number(programForm.menu_order) || 0,
      });
      setProgramModal(false);
      await fetchPrograms();
      if (res.data?.slug) {
        setSelectedSlug(res.data.slug);
      }
    } catch (err) {
      setFormError(err.response?.data ? JSON.stringify(err.response.data) : 'Не удалось создать');
    } finally {
      setSavingProgram(false);
    }
  }

  async function deleteProgram(slug) {
    if (!window.confirm(`Удалить программу «${slug}» и все темы?`)) return;
    try {
      await api.delete(`/tasks/topic-programs/manage/${encodeURIComponent(slug)}/`);
      if (selectedSlug === slug) setSelectedSlug(null);
      await fetchPrograms();
    } catch {
      setFormError('Не удалось удалить программу.');
    }
  }

  function openAddItem() {
    setEditingItemId(null);
    setItemForm({
      tag_slug: '',
      title: '',
      description: '',
      exam_number: '',
      study_order: 0,
    });
    setTagSearch('');
    setFormError('');
    setItemModal(true);
  }

  function openEditItem(item) {
    setEditingItemId(item.id);
    setItemForm({
      tag_slug: item.tag?.slug || '',
      title: item.title,
      description: item.description || '',
      exam_number: item.exam_number ?? '',
      study_order: item.study_order ?? 0,
    });
    setTagSearch('');
    setFormError('');
    setItemModal(true);
  }

  async function submitItem(e) {
    e.preventDefault();
    if (!selectedSlug) return;
    const payload = {
      tag_slug: itemForm.tag_slug.trim(),
      title: itemForm.title.trim(),
      description: itemForm.description,
      exam_number: itemForm.exam_number === '' ? null : Number(itemForm.exam_number),
      study_order: Number(itemForm.study_order) || 0,
    };
    setSavingProgram(true);
    setFormError('');
    try {
      if (editingItemId) {
        await api.patch(
          `/tasks/topic-programs/manage/${encodeURIComponent(selectedSlug)}/items/${editingItemId}/`,
          payload
        );
      } else {
        await api.post(
          `/tasks/topic-programs/manage/${encodeURIComponent(selectedSlug)}/items/`,
          payload
        );
      }
      setItemModal(false);
      await fetchDetail(selectedSlug);
      await fetchPrograms();
    } catch (err) {
      setFormError(err.response?.data ? JSON.stringify(err.response.data) : 'Ошибка сохранения темы');
    } finally {
      setSavingProgram(false);
    }
  }

  async function deleteItem(itemId) {
    if (!window.confirm('Удалить тему из программы?')) return;
    try {
      await api.delete(
        `/tasks/topic-programs/manage/${encodeURIComponent(selectedSlug)}/items/${itemId}/`
      );
      await fetchDetail(selectedSlug);
      await fetchPrograms();
    } catch {
      setFormError('Не удалось удалить тему.');
    }
  }

  function openAddType(itemId) {
    setTypeItemId(itemId);
    setTypeParentItemId(null);
    setEditingTypeId(null);
    setTypeForm({ tag_slug: '', label: '', sort_order: 0 });
    setTagSearch('');
    setFormError('');
    setTypeModal(true);
  }

  function openEditType(itemId, t) {
    setTypeItemId(null);
    setTypeParentItemId(itemId);
    setEditingTypeId(t.id);
    setTypeForm({
      tag_slug: t.tag?.slug || '',
      label: t.label || '',
      sort_order: t.sort_order ?? 0,
    });
    setTagSearch('');
    setFormError('');
    setTypeModal(true);
  }

  async function submitType(e) {
    e.preventDefault();
    if (!selectedSlug) return;
    setSavingProgram(true);
    setFormError('');
    try {
      const base = `/tasks/topic-programs/manage/${encodeURIComponent(selectedSlug)}/items`;
      const body = {
        tag_slug: typeForm.tag_slug.trim(),
        label: typeForm.label,
        sort_order: Number(typeForm.sort_order) || 0,
      };
      if (editingTypeId) {
        if (!typeParentItemId) throw new Error('no item');
        await api.patch(`${base}/${typeParentItemId}/types/${editingTypeId}/`, body);
      } else {
        await api.post(`${base}/${typeItemId}/types/`, body);
      }
      setTypeModal(false);
      await fetchDetail(selectedSlug);
    } catch (err) {
      setFormError(err.response?.data ? JSON.stringify(err.response.data) : 'Ошибка сохранения типа');
    } finally {
      setSavingProgram(false);
    }
  }

  async function deleteType(itemId, typeId) {
    if (!window.confirm('Удалить тип?')) return;
    try {
      await api.delete(
        `/tasks/topic-programs/manage/${encodeURIComponent(selectedSlug)}/items/${itemId}/types/${typeId}/`
      );
      await fetchDetail(selectedSlug);
    } catch {
      setFormError('Не удалось удалить тип.');
    }
  }

  return (
    <div className="admin-topic-programs-page">
      <div className="page-header">
        <h1>Тематические программы</h1>
        <p className="page-subtitle">
          ЕГЭ, ОГЭ и другие списки тем: привязка к тегам задач, подтипы, пункты меню.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={openCreateProgram}>
          Новая программа
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {formError && !programModal && !itemModal && !typeModal && (
        <div className="alert alert-error">{formError}</div>
      )}

      {loading ? (
        <p className="text-secondary">Загрузка...</p>
      ) : (
        <div className="admin-topic-programs-layout">
          <aside className="admin-topic-programs-aside">
            <h2 style={{ fontSize: '1rem', marginTop: 0 }}>Программы</h2>
            <ul className="admin-topic-programs-list">
              {programs.map((p) => (
                <li key={p.slug}>
                  <button
                    type="button"
                    className={`btn btn-text admin-topic-programs-pick ${selectedSlug === p.slug ? 'active' : ''}`}
                    onClick={() => setSelectedSlug(p.slug)}
                  >
                    {p.title}
                    <span className="text-secondary" style={{ fontSize: '0.8rem', marginLeft: '0.35rem' }}>
                      ({p.slug})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {programs.length === 0 && <p className="text-secondary">Пока нет программ.</p>}
          </aside>

          <section className="admin-topic-programs-main">
            {!selectedSlug && <p className="text-secondary">Выберите программу слева.</p>}
            {selectedSlug && detailLoading && <p className="text-secondary">Загрузка...</p>}
            {selectedSlug && detail && !detailLoading && (
              <>
                <div className="admin-topic-programs-panel">
                  <h2 style={{ marginTop: 0 }}>Параметры</h2>
                  <div className="form-group">
                    <label>Заголовок</label>
                    <input
                      className="form-control"
                      value={detail.title}
                      onChange={(e) => setDetail({ ...detail, title: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Описание</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={detail.description || ''}
                      onChange={(e) => setDetail({ ...detail, description: e.target.value })}
                    />
                  </div>
                  <div className="form-group checkbox-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!detail.show_in_menu}
                        onChange={(e) => setDetail({ ...detail, show_in_menu: e.target.checked })}
                      />
                      Показывать в меню сайта
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Подпись в меню</label>
                    <input
                      className="form-control"
                      placeholder="Если пусто — используется заголовок"
                      value={detail.menu_label || ''}
                      onChange={(e) => setDetail({ ...detail, menu_label: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Порядок в меню</label>
                    <input
                      type="number"
                      className="form-control"
                      value={detail.menu_order ?? 0}
                      onChange={(e) =>
                        setDetail({ ...detail, menu_order: Number(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={savingProgram}
                      onClick={saveProgramMeta}
                    >
                      Сохранить
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => deleteProgram(detail.slug)}
                    >
                      Удалить программу
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={openAddItem}>
                      Добавить тему
                    </button>
                  </div>
                </div>

                <h2>Темы</h2>
                {(detail.items || []).length === 0 && (
                  <p className="text-secondary">Тем нет — добавьте тег и название.</p>
                )}
                <ul className="admin-topic-program-items">
                  {(detail.items || []).map((item) => (
                    <li key={item.id} className="admin-topic-programs-panel">
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <strong>{item.title}</strong>
                          <span className="text-secondary" style={{ marginLeft: '0.5rem' }}>
                            {item.tag?.slug}
                          </span>
                          {item.exam_number != null && (
                            <span className="badge" style={{ marginLeft: '0.5rem' }}>
                              экз. №{item.exam_number}
                            </span>
                          )}
                          <span className="text-secondary" style={{ marginLeft: '0.5rem' }}>
                            порядок {item.study_order}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button
                            type="button"
                            className="btn btn-outline btn-xs"
                            onClick={() => openEditItem(item)}
                          >
                            Правка
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-xs"
                            onClick={() => deleteItem(item.id)}
                          >
                            Удалить
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-xs"
                            onClick={() => openAddType(item.id)}
                          >
                            + тип
                          </button>
                        </div>
                      </div>
                      {item.description ? (
                        <p className="text-secondary" style={{ marginBottom: '0.5rem', whiteSpace: 'pre-wrap' }}>
                          {item.description}
                        </p>
                      ) : null}
                      {(item.types || []).length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                          {(item.types || []).map((t) => (
                            <li key={t.id}>
                              {t.label || t.tag?.name} ({t.tag?.slug})
                              <button
                                type="button"
                                className="btn btn-text btn-xs"
                                onClick={() => openEditType(item.id, t)}
                              >
                                правка
                              </button>
                              <button
                                type="button"
                                className="btn btn-text btn-xs"
                                onClick={() => deleteType(item.id, t.id)}
                              >
                                удалить
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </div>
      )}

      <Modal open={programModal} onClose={() => setProgramModal(false)} title="Новая программа">
        <form onSubmit={submitCreateProgram}>
          {formError && <div className="alert alert-error">{formError}</div>}
          <div className="form-group">
            <label>Slug (URL)</label>
            <input
              className="form-control"
              required
              value={programForm.slug}
              onChange={(e) => setProgramForm({ ...programForm, slug: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Заголовок</label>
            <input
              className="form-control"
              required
              value={programForm.title}
              onChange={(e) => setProgramForm({ ...programForm, title: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Описание</label>
            <textarea
              className="form-control"
              rows={2}
              value={programForm.description}
              onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
            />
          </div>
          <div className="form-group checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={programForm.show_in_menu}
                onChange={(e) => setProgramForm({ ...programForm, show_in_menu: e.target.checked })}
              />
              В меню
            </label>
          </div>
          <div className="form-group">
            <label>Подпись в меню</label>
            <input
              className="form-control"
              value={programForm.menu_label}
              onChange={(e) => setProgramForm({ ...programForm, menu_label: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Порядок в меню</label>
            <input
              type="number"
              className="form-control"
              value={programForm.menu_order}
              onChange={(e) => setProgramForm({ ...programForm, menu_order: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={savingProgram}>
              Создать
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={itemModal}
        onClose={() => setItemModal(false)}
        title={editingItemId ? 'Правка темы' : 'Новая тема'}
      >
        <form onSubmit={submitItem}>
          {formError && <div className="alert alert-error">{formError}</div>}
          <div className="form-group">
            <label>Slug тега (тема)</label>
            <input
              className="form-control"
              required
              value={itemForm.tag_slug}
              onChange={(e) => setItemForm({ ...itemForm, tag_slug: e.target.value })}
            />
            <input
              className="form-control"
              style={{ marginTop: '0.35rem' }}
              placeholder="Поиск тега…"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
            />
            {tagSuggestions.length > 0 && (
              <ul className="tag-suggest-list">
                {tagSuggestions.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className="btn btn-text btn-sm"
                      onClick={() => {
                        setItemForm((f) => ({ ...f, tag_slug: t.slug }));
                        setTagSearch('');
                        setTagSuggestions([]);
                      }}
                    >
                      {t.name} — {t.slug}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="form-group">
            <label>Название пункта</label>
            <input
              className="form-control"
              required
              value={itemForm.title}
              onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Описание</label>
            <textarea
              className="form-control"
              rows={2}
              value={itemForm.description}
              onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Номер в экзамене (опционально)</label>
            <input
              type="number"
              className="form-control"
              value={itemForm.exam_number}
              onChange={(e) => setItemForm({ ...itemForm, exam_number: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Порядок изучения</label>
            <input
              type="number"
              className="form-control"
              value={itemForm.study_order}
              onChange={(e) => setItemForm({ ...itemForm, study_order: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={savingProgram}>
              Сохранить
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={typeModal}
        onClose={() => setTypeModal(false)}
        title={editingTypeId ? 'Правка типа' : 'Новый тип'}
      >
        <form onSubmit={submitType}>
          {formError && <div className="alert alert-error">{formError}</div>}
          <div className="form-group">
            <label>Slug тега (тип)</label>
            <input
              className="form-control"
              required
              value={typeForm.tag_slug}
              onChange={(e) => setTypeForm({ ...typeForm, tag_slug: e.target.value })}
            />
            <input
              className="form-control"
              style={{ marginTop: '0.35rem' }}
              placeholder="Поиск тега…"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
            />
            {tagSuggestions.length > 0 && (
              <ul className="tag-suggest-list">
                {tagSuggestions.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className="btn btn-text btn-sm"
                      onClick={() => {
                        setTypeForm((f) => ({ ...f, tag_slug: t.slug }));
                        setTagSearch('');
                        setTagSuggestions([]);
                      }}
                    >
                      {t.name} — {t.slug}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="form-group">
            <label>Подпись (опционально)</label>
            <input
              className="form-control"
              value={typeForm.label}
              onChange={(e) => setTypeForm({ ...typeForm, label: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Порядок сортировки</label>
            <input
              type="number"
              className="form-control"
              value={typeForm.sort_order}
              onChange={(e) => setTypeForm({ ...typeForm, sort_order: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={savingProgram}>
              Сохранить
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
